/**
 * PWS × AWARE Conference Interest Landing (Feb 2026 — July release)
 * =================================================================
 *
 * Public route: /pws-partner
 *
 * Reached by scanning the printed conference QR code at the end of the
 * PWS franchisee presentation. Captures a short expression of interest,
 * fires two emails (branded Partner Guide → franchisee, ops alert →
 * admin), and stores the record in `pws_partner_interest` for the admin
 * dashboard to action later. No SMS. No auto-invite. No auto-portal
 * activation. Feature-flag gated — while `pws_partner_conference_
 * landing` is at `internal_only`, only signed-in admins/lab users see
 * this page; anonymous visitors see a graceful "coming soon" state.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, Beaker, FileText, LineChart, ShieldCheck,
  ArrowRight, Sparkles, Loader2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { pushFormEvent } from "@/lib/analytics";
import { BrandLogo } from "@/components/BrandLogo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const BENEFIT_ICONS = [Beaker, FileText, LineChart, ShieldCheck];

export default function PwsPartnerConferenceLanding() {
  const navigate = useNavigate();
  useSEO({
    title: "PWS × AWARE Partner Network",
    description: "Professional water testing made simple for Pristine Water Systems franchisees.",
    noindex: true,
    includeBaseline: false,
  });
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    franchise_region: "",
    email: "",
    mobile: "",
    questions: "",
    consent_guide: false,
    consent_portal_contact: false,
    website: "", // honeypot — hidden
  });
  const formRef = useRef(null);
  const analyticsPingedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/pws-partner/config");
        if (!cancelled) setConfig(data);
      } catch (e) {
        if (!cancelled) setConfig({ visible_to_me: false, flag_status: "unknown" });
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fire landing_page_viewed once per visit — only after we know the
  // flag is at least internal_only for this visitor (never PII).
  useEffect(() => {
    if (!config?.visible_to_me || analyticsPingedRef.current) return;
    analyticsPingedRef.current = true;
    api.post("/pws-partner/analytics", {
      event: "landing_page_viewed",
      referrer: (document.referrer || "").slice(0, 200),
    }).catch(() => {});
  }, [config]);

  const canSubmit = useMemo(() => {
    return (
      form.first_name.trim() &&
      form.last_name.trim() &&
      form.franchise_region.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
      /^\+?[\d\s\-]{6,20}$/.test(form.mobile.trim()) &&
      form.consent_guide &&
      !submitting
    );
  }, [form, submitting]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setErrorMsg(null);
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        franchise_region: form.franchise_region.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        questions: form.questions.trim() || null,
        consent_guide: form.consent_guide,
        consent_portal_contact: form.consent_portal_contact,
        website: form.website, // honeypot — MUST stay empty
      };
      const { data } = await api.post("/pws-partner-interest", payload);
      pushFormEvent("contact_form_submit", {
        form_name: "pws_partner_interest",
        form_type: "partner_interest",
      });
      navigate("/pws-partner/thank-you", {
        replace: true,
        state: {
          emailSent: data?.email_sent !== false,
          serverMessage: data?.message || null,
        },
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        setErrorMsg("A lot of enquiries just came through — please try again in a minute.");
      } else if (detail === "consent_guide_required") {
        setErrorMsg("Please tick the box confirming you'd like to receive the Partner Guide.");
      } else {
        setErrorMsg(formatApiError(err));
      }
      toast.error(errorMsg || "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function scrollToForm() {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const copy = config?.landing_copy || {};

  // ── OFF / not-visible state ──────────────────────────────────────
  if (!loadingConfig && !config?.visible_to_me) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-6 py-24">
          <div
            data-testid="pws-conference-offline"
            className="max-w-lg text-center bg-white rounded-2xl border border-slate-200 p-10 shadow-sm"
          >
            <BrandLogo className="mx-auto mb-6 h-12 w-auto" />
            <h1 className="text-2xl font-semibold text-slate-900">
              The PWS × AWARE Partner Network page is coming soon.
            </h1>
            <p className="mt-4 text-slate-600">
              Please check back after the conference or contact the AWARE Water
              Testing team directly.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 text-cyan-700 font-medium hover:underline"
            >
              Return to awarewatertesting.com.au <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#031533] via-[#062754] to-[#0a3878] text-white">
      {/* ─── Hero ───────────────────────────────────────────────── */}
      <div className="mx-auto max-w-6xl px-6 pt-10 md:pt-14 pb-16 md:pb-20" data-testid="pws-conference-landing">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-9 w-auto" />
            <span className="text-sm text-cyan-200/80 hidden md:inline">
              awarewatertesting.com.au
            </span>
          </div>
          {config?.flag_status === "internal_only" && (
            <span
              data-testid="pws-conference-preview-badge"
              className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-amber-300/60 bg-amber-500/15 text-amber-100"
            >
              Preview — internal only
            </span>
          )}
        </div>

        <div className="mt-14 md:mt-16 grid gap-10 md:grid-cols-[minmax(0,1fr)_360px] items-start">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-3 py-1 text-xs uppercase tracking-[0.14em] text-cyan-100">
              <Sparkles className="h-3.5 w-3.5" />
              PWS × AWARE Partner Network
            </div>
            <h1
              data-testid="pws-conference-heading"
              className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight"
            >
              {copy.heading || "PWS × AWARE Partner Network"}
            </h1>
            <p
              data-testid="pws-conference-intro"
              className="mt-6 text-lg text-cyan-50/85 whitespace-pre-line leading-relaxed max-w-2xl"
            >
              {copy.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={scrollToForm}
                data-testid="pws-conference-cta-primary"
                className="inline-flex items-center gap-2 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold px-6 py-3 rounded-full transition-colors"
              >
                {copy.cta_label || "Get the Partner Guide"} <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="mailto:admin@awarewatertesting.com.au"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/25 text-white/90 hover:bg-white/10 transition-colors text-sm"
              >
                Talk to Stephen
              </a>
            </div>
          </div>

          {/* Right-hand benefits card (asymmetric layout, not centered) */}
          <div
            data-testid="pws-conference-benefits"
            className="rounded-2xl border border-cyan-200/15 bg-white/5 backdrop-blur-sm p-6 md:p-7 shadow-2xl shadow-cyan-950/40"
          >
            <div className="text-[11px] uppercase tracking-[0.18em] text-cyan-200 font-semibold">
              Why partner with us
            </div>
            <ul className="mt-4 space-y-3.5">
              {(copy.benefits || []).map((b, i) => {
                const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
                return (
                  <li key={b} className="flex items-start gap-3 text-cyan-50/90 text-sm">
                    <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-cyan-400/15 text-cyan-200">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span>{b}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      {/* ─── Form panel ─────────────────────────────────────────── */}
      <div
        ref={formRef}
        className="bg-white text-slate-900 rounded-t-[32px] md:rounded-t-[48px] shadow-2xl"
      >
        <div className="mx-auto max-w-3xl px-6 py-14 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Send me the Partner Guide
            </h2>
            <p className="mt-4 text-slate-600 text-base">
              Fill in your details and we&apos;ll email the digital Partner Guide
              straight away. Stephen or the AWARE team will follow up if you
              also asked about the Partner Portal.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="pws-conference-form"
            className="mt-10 space-y-6"
            noValidate
          >
            {/* Hidden honeypot — real users never see this */}
            <div style={{ position: "absolute", left: "-10000px", height: 0, overflow: "hidden" }}>
              <label>
                Website <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => updateField("website", e.target.value)}
                />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="First name" required>
                <input
                  data-testid="pws-conference-first-name"
                  type="text" required maxLength={80}
                  value={form.first_name}
                  onChange={(e) => updateField("first_name", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Last name" required>
                <input
                  data-testid="pws-conference-last-name"
                  type="text" required maxLength={80}
                  value={form.last_name}
                  onChange={(e) => updateField("last_name", e.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>

            <Field label="PWS franchise or region" required>
              <input
                data-testid="pws-conference-franchise-region"
                type="text" required maxLength={200}
                placeholder="e.g. Noosa, Sunshine Coast, Toowoomba"
                value={form.franchise_region}
                onChange={(e) => updateField("franchise_region", e.target.value)}
                className={inputClass}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Email address" required>
                <input
                  data-testid="pws-conference-email"
                  type="email" required inputMode="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Mobile number" required hint="For partner contact only — we don't send SMS marketing.">
                <input
                  data-testid="pws-conference-mobile"
                  type="tel" required inputMode="tel"
                  autoComplete="tel"
                  placeholder="+61 400 000 000"
                  value={form.mobile}
                  onChange={(e) => updateField("mobile", e.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>

            <Field label="Questions or comments" hint="Optional">
              <textarea
                data-testid="pws-conference-questions"
                rows={4} maxLength={4000}
                value={form.questions}
                onChange={(e) => updateField("questions", e.target.value)}
                className={`${inputClass} resize-y`}
              />
            </Field>

            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                <input
                  data-testid="pws-conference-consent-guide"
                  type="checkbox" required
                  checked={form.consent_guide}
                  onChange={(e) => updateField("consent_guide", e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                />
                <span>
                  <b className="text-slate-900">Required.</b>{" "}
                  {copy.consent_guide_text}
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                <input
                  data-testid="pws-conference-consent-portal"
                  type="checkbox"
                  checked={form.consent_portal_contact}
                  onChange={(e) => updateField("consent_portal_contact", e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                />
                <span>
                  <b className="text-slate-900">Optional.</b>{" "}
                  {copy.consent_portal_contact_text}
                </span>
              </label>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed">
              {copy.privacy_statement ||
                "Your details will be used only to provide information about the AWARE Partner Network and manage your partner enquiry. You can unsubscribe from marketing emails at any time."}
            </p>

            {errorMsg && (
              <div
                data-testid="pws-conference-error"
                className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3"
              >
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="pws-conference-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-full text-base transition-colors"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
              ) : (
                <><CheckCircle2 className="h-4 w-4" /> Send Me the Partner Guide</>
              )}
            </button>
          </form>
        </div>
      </div>

      <div className="bg-slate-50 text-slate-500">
        <Footer />
      </div>
    </div>
  );
}

// ─── Field helper ──────────────────────────────────────────────────
const inputClass =
  "w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent";

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-slate-900 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </label>
  );
}
